/* eslint-disable react/require-default-props, @typescript-eslint/ban-types */
import { Box } from '@chakra-ui/core';
import React, { memo, ComponentType, useMemo } from 'react';

const FOCUS_STYLE = { boxShadow: '0 0 0 2px rgba(66,153,225,0.4)' } as const;
const HOVER_ACTIVE = { borderColor: 'blue.500', bg: 'blue.50' } as const;
const HOVER_INACTIVE = { borderColor: 'gray.300', bg: 'gray.50' } as const;
const HOVER_DISABLED = {} as const;

const BASE_STYLES = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  flex: '1',
  borderRadius: 'md',
  border: '3px solid',
  transition: 'all 0.15s ease',
  padding: '1px',
  _focus: FOCUS_STYLE,
  width: '100%',
  aspectRatio: '1/1',
  height: '-webkit-fill-available',
} as const;

const ACTIVE_STYLES = {
  borderColor: 'blue.500',
  bg: 'blue.100',
  outline: '3px solid rgba(212, 216, 241, 1)',
  _hover: HOVER_ACTIVE,
} as const;

const INACTIVE_STYLES = {
  borderColor: 'gray.50',
  bg: 'gray.50',
  outline: 'none',
  _hover: HOVER_INACTIVE,
} as const;

const APPLYING_STYLES = {
  cursor: 'wait',
  _hover: HOVER_DISABLED,
} as const;

const IDLE_CURSOR = { cursor: 'pointer' } as const;

interface EffectCardProps {
  isActive?: boolean;
  isApplying?: boolean;
}

function getStateStyles(isActive: boolean, isApplying: boolean) {
  return {
    ...(isActive ? ACTIVE_STYLES : INACTIVE_STYLES),
    ...(isApplying ? APPLYING_STYLES : IDLE_CURSOR),
  };
}

export function withEffectCardWrapper<P extends object>(WrappedComponent: ComponentType<P>) {
  const MemoizedWrappedComponent = memo(WrappedComponent);
  const displayName = WrappedComponent.displayName || WrappedComponent.name || /* istanbul ignore next */ 'Component';

  const WithEffectCardWrapper = memo(function WithEffectCardWrapper(props: P & EffectCardProps) {
    const { isActive = false, isApplying = false } = props ?? /* istanbul ignore next */ ({} as P & EffectCardProps);

    const stateStyles = useMemo(() => getStateStyles(isActive, isApplying), [isActive, isApplying]);

    const boxProps = useMemo(() => ({ ...BASE_STYLES, ...stateStyles }), [stateStyles]);

    return (
      <Box {...boxProps} className="effect-card-wrapper" sx={{ aspectRatio: '1/1' }}>
        <MemoizedWrappedComponent {...(props as P)} />
      </Box>
    );
  });

  WithEffectCardWrapper.displayName = `withEffectCardWrapper(${displayName})`;

  return WithEffectCardWrapper;
}
